/**
 * Pagix — Standalone Template Generator & Cloner Studio Playground
 * Industrial-grade UI for 10 Awwwards Archetypes & Website Style Cloner
 */

const state = {
  activeTab: 'templates',
  activeDevice: 'desktop',
  lang: 'en',
  templates: [],
  selectedTemplate: null,
  extractedProfile: null,
};

// ── Helper: Escape HTML ─────────────────────────────────────────────────────
function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ── Ensure Google Fonts are loaded dynamically ──────────────────────────────
function ensureGoogleFont(fontName) {
  if (!fontName) return;
  const id = `gfont-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400;500;600;700;800&display=swap`;
  document.head.appendChild(link);
}

// ── Color Utilities ─────────────────────────────────────────────────────────
function colorMix(hex, percent) {
  return `color-mix(in srgb, ${hex} ${percent}%, transparent)`;
}

function getContrastTextColor(hex) {
  const clean = (hex || '#ffffff').replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16) || 0;
  const g = parseInt(clean.substring(2, 4), 16) || 0;
  const b = parseInt(clean.substring(4, 6), 16) || 0;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 140 ? '#0b0d11' : '#ffffff';
}

function rgbToHex(val) {
  if (!val) return '#0f6cbd';
  if (val.startsWith('#') && (val.length === 7 || val.length === 4)) {
    if (val.length === 4) {
      return `#${val[1]}${val[1]}${val[2]}${val[2]}${val[3]}${val[3]}`;
    }
    return val;
  }
  return '#0f6cbd';
}

// ── Tab Navigation ──────────────────────────────────────────────────────────
function initTabs() {
  const tabTemplates = document.getElementById('tab-btn-templates');
  const tabCloner = document.getElementById('tab-btn-cloner');
  const paneTemplates = document.getElementById('pane-templates');
  const paneCloner = document.getElementById('pane-cloner');

  tabTemplates.addEventListener('click', () => {
    state.activeTab = 'templates';
    tabTemplates.setAttribute('aria-selected', 'true');
    tabCloner.setAttribute('aria-selected', 'false');
    paneTemplates.style.display = 'flex';
    paneTemplates.hidden = false;
    paneCloner.style.display = 'none';
    paneCloner.hidden = true;
  });

  tabCloner.addEventListener('click', () => {
    state.activeTab = 'cloner';
    tabCloner.setAttribute('aria-selected', 'true');
    tabTemplates.setAttribute('aria-selected', 'false');
    paneCloner.style.display = 'flex';
    paneCloner.hidden = false;
    paneTemplates.style.display = 'none';
    paneTemplates.hidden = true;
  });

  // Preset Site Buttons
  document.querySelectorAll('.preset-site-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const url = btn.dataset.url;
      const input = document.getElementById('input-site-url');
      if (input) input.value = url;
    });
  });
}

// ── Collapsible Brief Settings ──────────────────────────────────────────────
function initBriefToggle() {
  const toggleBtn = document.getElementById('brief-config-toggle');
  const card = document.getElementById('brief-config-card');
  if (toggleBtn && card) {
    toggleBtn.addEventListener('click', () => {
      card.classList.toggle('collapsed');
    });
  }
}

// ── Device Switcher ─────────────────────────────────────────────────────────
function initDeviceSwitcher() {
  const frame = document.getElementById('sim-frame');
  const buttons = document.querySelectorAll('.device-btn');

  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const device = btn.dataset.device;
      state.activeDevice = device;
      frame.dataset.device = device;
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
    });
  });
}

// ── Live Preview Renderer ───────────────────────────────────────────────────
export function renderLivePreview(spec) {
  if (!spec) return;

  const theme = spec.theme || {};
  const header = spec.header || {};
  const footer = spec.footer || {};
  const hero = spec.hero || spec.sampleHero || {};

  const primary = theme.primary || '#0f6cbd';
  const secondary = theme.secondary || '#111318';
  const surface = theme.surface || '#0b0d11';
  const textStrong = theme.textStrong || '#f8fafc';
  const textSub = theme.textSub || '#94a3b8';
  const headingFont = theme.headingFont || theme.font || 'Inter';
  const bodyFont = theme.font || 'Inter';
  const archetype = theme.archetype || spec.id || 'dark-minimal-saas';

  ensureGoogleFont(headingFont);
  ensureGoogleFont(bodyFont);

  const frame = document.getElementById('sim-frame');
  const headerEl = document.getElementById('sim-header-el');
  const heroEl = document.getElementById('sim-hero-el');
  const bentoEl = document.getElementById('sim-bento-el');
  const testimonialEl = document.getElementById('sim-testimonial-card');
  const footerEl = document.getElementById('sim-footer-el');

  // Update Toolbar Meta
  const nameEl = document.getElementById('active-archetype-name');
  if (nameEl) nameEl.textContent = spec.name || theme.archetype || 'Özel Şablon';

  const motionEl = document.getElementById('active-motion-badge');
  if (motionEl) {
    motionEl.textContent = spec.motion?.scrollReveal
      ? `GSAP: data-scroll-reveal="${spec.motion.scrollReveal}"`
      : 'GSAP: ScrollTrigger Reveal';
  }

  // Update Browser Address Bar Mock URL
  const urlBar = document.getElementById('browser-url-text');
  if (urlBar) {
    const slug = (spec.brandName || spec.id || 'nova-ai')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    urlBar.textContent = `https://${slug}.com/preview`;
  }

  // Frame Background & Base Typography
  frame.style.backgroundColor = surface;
  frame.style.color = textStrong;
  frame.style.fontFamily = `"${bodyFont}", system-ui, sans-serif`;

  // ── Archetype-Specific Geometry & Stylings ─────────────────────────────
  const isNeoBrutalist = archetype === 'neo-brutalist';
  const isGlassmorphic = archetype === 'glassmorphic-glow';
  const isDarkMinimal = archetype === 'dark-minimal-saas';
  const isCleanSoftTech = archetype === 'clean-soft-tech';
  const isMonospace = archetype === 'developer-monospace';

  let borderRadiusBase = '12px';
  let buttonRadius = '8px';
  let cardShadow = '0 6px 20px rgba(0, 0, 0, 0.1)';

  if (isNeoBrutalist) {
    borderRadiusBase = '0px';
    buttonRadius = '0px';
    cardShadow = '4px 4px 0px #000000';
  } else if (theme.radius === 'none') {
    borderRadiusBase = '0px';
    buttonRadius = '0px';
  } else if (theme.radius === 'full') {
    borderRadiusBase = '24px';
    buttonRadius = '999px';
  } else if (theme.radius === 'lg') {
    borderRadiusBase = '18px';
    buttonRadius = '12px';
  }

  // Header Styling
  const headerStyle = header.style || 'floating-pill';
  headerEl.className = `sim-header ${headerStyle}`;
  headerEl.style.color = textStrong;
  headerEl.style.borderRadius = headerStyle === 'floating-pill' ? (isNeoBrutalist ? '0px' : '999px') : '0px';

  if (isNeoBrutalist) {
    headerEl.style.border = '2px solid #000000';
    headerEl.style.boxShadow = cardShadow;
    headerEl.style.backgroundColor = surface;
  } else if (isGlassmorphic) {
    headerEl.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
    headerEl.style.backdropFilter = 'blur(16px)';
    headerEl.style.border = `1px solid ${colorMix(primary, 30)}`;
  } else if (headerStyle === 'floating-pill') {
    headerEl.style.backgroundColor = surface === '#ffffff' ? 'rgba(255, 255, 255, 0.88)' : 'rgba(20, 24, 33, 0.85)';
    headerEl.style.backdropFilter = 'blur(16px)';
    headerEl.style.borderColor = colorMix(textStrong, 14);
  } else {
    headerEl.style.backgroundColor = surface;
    headerEl.style.borderColor = colorMix(textStrong, 12);
  }

  // Logo & Nav Links
  const logoEl = document.getElementById('sim-logo-text');
  logoEl.textContent = spec.brandName || spec.id || 'Pagix';
  logoEl.style.fontFamily = `"${headingFont}", sans-serif`;

  const navLinksEl = document.getElementById('sim-nav-links');
  // header.navLinks Website Cloner ile gelen gerçek bir siteden çıkarılmış
  // olabilir; extraction hiçbir link bulamazsa boş bir dizi ([]) döner —
  // bu da truthy olduğu için `||` fallback'i tetiklemez ve önizleme boş
  // görünür. Bu yüzden burada dizi UZUNLUĞUNU kontrol ediyoruz.
  const links = header.navLinks?.length ? header.navLinks : [
    { label: 'Features', href: '#features' },
    { label: 'How it Works', href: '#how-it-works' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'FAQ', href: '#faq' },
  ];
  navLinksEl.innerHTML = links
    .map((l) => `<span style="color: ${textSub}; cursor: pointer; transition: color 0.15s ease;">${escapeHtml(l.label)}</span>`)
    .join('');

  // Nav CTA
  const ctaBtn = document.getElementById('sim-cta-btn');
  const ctaLabel = header.cta?.label || spec.primaryCta || 'Get Started';
  ctaBtn.textContent = ctaLabel;
  ctaBtn.style.backgroundColor = primary;
  ctaBtn.style.color = getContrastTextColor(primary);
  ctaBtn.style.borderRadius = buttonRadius;
  if (isNeoBrutalist) {
    ctaBtn.style.border = '2px solid #000000';
    ctaBtn.style.boxShadow = '2px 2px 0px #000000';
  } else {
    ctaBtn.style.border = 'none';
    ctaBtn.style.boxShadow = `0 4px 14px ${colorMix(primary, 40)}`;
  }

  // ── Hero Section ───────────────────────────────────────────────────────
  heroEl.style.color = textStrong;

  // Badge
  const badgeEl = document.getElementById('sim-hero-badge');
  badgeEl.textContent = hero.badge || '✨ 2026 AI Innovation Award';
  badgeEl.style.backgroundColor = isNeoBrutalist ? '#ffffff' : colorMix(primary, 14);
  badgeEl.style.color = isNeoBrutalist ? '#000000' : primary;
  badgeEl.style.border = isNeoBrutalist ? '2px solid #000000' : `1px solid ${colorMix(primary, 28)}`;
  badgeEl.style.borderRadius = isNeoBrutalist ? '0px' : '999px';
  if (isNeoBrutalist) badgeEl.style.boxShadow = '2px 2px 0px #000000';

  // Headline
  const headlineEl = document.getElementById('sim-hero-headline');
  const brandName = spec.brandName || 'Nova AI';
  const offer = spec.offer || 'Smart Workflows Built for the Future';
  headlineEl.textContent = hero.headline || `${offer} with ${brandName}`;
  headlineEl.style.fontFamily = `"${headingFont}", sans-serif`;
  if (isMonospace) {
    headlineEl.style.letterSpacing = '-0.04em';
  }

  // Subheadline
  const subheadlineEl = document.getElementById('sim-hero-subheadline');
  subheadlineEl.textContent = hero.subheadline || 'AI infrastructure that automates your team\'s workflows and delivers a 40% productivity gain.';
  subheadlineEl.style.color = textSub;

  // Hero CTAs
  const heroPrimaryCta = document.getElementById('sim-hero-primary-cta');
  heroPrimaryCta.textContent = ctaLabel;
  heroPrimaryCta.style.backgroundColor = primary;
  heroPrimaryCta.style.color = getContrastTextColor(primary);
  heroPrimaryCta.style.borderRadius = buttonRadius;
  if (isNeoBrutalist) {
    heroPrimaryCta.style.border = '2px solid #000000';
    heroPrimaryCta.style.boxShadow = '3px 3px 0px #000000';
  } else {
    heroPrimaryCta.style.border = 'none';
    heroPrimaryCta.style.boxShadow = `0 6px 20px ${colorMix(primary, 45)}`;
  }

  const heroSecondaryCta = document.getElementById('sim-hero-secondary-cta');
  heroSecondaryCta.textContent = hero.secondaryCta || 'Watch Demo →';
  heroSecondaryCta.style.color = textStrong;
  heroSecondaryCta.style.borderRadius = buttonRadius;
  heroSecondaryCta.style.backgroundColor = isNeoBrutalist ? '#ffffff' : colorMix(textStrong, 4);
  heroSecondaryCta.style.border = isNeoBrutalist ? '2px solid #000000' : `1px solid ${colorMix(textStrong, 18)}`;
  if (isNeoBrutalist) heroSecondaryCta.style.boxShadow = '3px 3px 0px #000000';

  // Proof Strip
  const proofStrip = document.getElementById('sim-proof-strip');
  if (proofStrip) {
    proofStrip.style.color = textSub;
  }

  // ── Bento Grid ─────────────────────────────────────────────────────────
  const bentoCards = bentoEl.querySelectorAll('.sim-bento-card');
  bentoCards.forEach((card) => {
    card.style.borderRadius = borderRadiusBase;
    card.style.color = textStrong;

    if (isNeoBrutalist) {
      card.style.backgroundColor = '#ffffff';
      card.style.border = '2px solid #000000';
      card.style.boxShadow = cardShadow;
    } else if (isGlassmorphic) {
      card.style.backgroundColor = 'rgba(255, 255, 255, 0.04)';
      card.style.backdropFilter = 'blur(16px)';
      card.style.border = `1px solid ${colorMix(primary, 22)}`;
    } else {
      card.style.backgroundColor = colorMix(textStrong, 4);
      card.style.border = `1px solid ${colorMix(textStrong, 10)}`;
      card.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.06)';
    }

    const title = card.querySelector('h3');
    if (title) {
      title.style.fontFamily = `"${headingFont}", sans-serif`;
      title.style.color = textStrong;
    }
    const para = card.querySelector('p');
    if (para) para.style.color = textSub;
  });

  // ── Testimonial Card ───────────────────────────────────────────────────
  if (testimonialEl) {
    testimonialEl.style.borderRadius = borderRadiusBase;
    testimonialEl.style.color = textStrong;

    if (isNeoBrutalist) {
      testimonialEl.style.backgroundColor = '#ffffff';
      testimonialEl.style.border = '2px solid #000000';
      testimonialEl.style.boxShadow = cardShadow;
    } else if (isGlassmorphic) {
      testimonialEl.style.backgroundColor = 'rgba(255, 255, 255, 0.04)';
      testimonialEl.style.backdropFilter = 'blur(16px)';
      testimonialEl.style.border = `1px solid ${colorMix(primary, 22)}`;
    } else {
      testimonialEl.style.backgroundColor = colorMix(textStrong, 3);
      testimonialEl.style.border = `1px solid ${colorMix(textStrong, 10)}`;
    }

    const quoteText = document.getElementById('sim-quote-text');
    if (quoteText) quoteText.style.fontFamily = `"${headingFont}", sans-serif`;
  }

  // ── Footer ─────────────────────────────────────────────────────────────
  footerEl.className = `sim-footer ${footer.style || 'editorial-columns'}`;
  footerEl.style.borderColor = colorMix(textStrong, 10);
  footerEl.style.backgroundColor = isNeoBrutalist ? '#ffffff' : colorMix(textStrong, 2);

  const footerColsEl = document.getElementById('sim-footer-cols');
  // Aynı neden: klonlanan sitede footer kolonu bulunamazsa footer.columns
  // [] (boş dizi) olur; uzunluğa bakmadan `||` kullanmak boş bir footer
  // bırakırdı.
  const footerCols = footer.columns?.length ? footer.columns : [
    { title: 'Product', links: [{ label: 'Features' }, { label: 'Integrations' }, { label: 'Pricing' }] },
    { title: 'Resources', links: [{ label: 'Documentation' }, { label: 'API' }, { label: 'Guides' }] },
    { title: 'Company', links: [{ label: 'About' }, { label: 'Careers' }, { label: 'Contact' }] },
    { title: 'Legal', links: [{ label: 'Privacy' }, { label: 'Terms' }, { label: 'Security' }] },
  ];
  footerColsEl.innerHTML = footerCols
    .map(
      (c) => `
    <div class="sim-footer-col">
      <h4 style="color: ${textStrong}; font-family: '${headingFont}', sans-serif;">${escapeHtml(c.title)}</h4>
      <ul>
        ${(c.links || []).map((l) => `<li style="color: ${textSub};">${escapeHtml(l.label || l)}</li>`).join('')}
      </ul>
    </div>
  `,
    )
    .join('');

  const copyrightEl = document.getElementById('sim-copyright-text');
  if (copyrightEl) {
    copyrightEl.textContent = footer.copyright || `© 2026 ${spec.brandName || 'Pagix'}. All rights reserved.`;
    copyrightEl.style.color = textSub;
  }

  // ── Update Floating Token Tuner Values ─────────────────────────────────
  const primaryInput = document.getElementById('tune-primary-color');
  if (primaryInput) primaryInput.value = rgbToHex(primary);
  const primaryHex = document.getElementById('tune-primary-hex');
  if (primaryHex) primaryHex.textContent = primary;

  const secondaryInput = document.getElementById('tune-secondary-color');
  if (secondaryInput) secondaryInput.value = rgbToHex(secondary);
  const secondaryHex = document.getElementById('tune-secondary-hex');
  if (secondaryHex) secondaryHex.textContent = secondary;

  const surfaceInput = document.getElementById('tune-surface-color');
  if (surfaceInput) surfaceInput.value = rgbToHex(surface);
  const surfaceHex = document.getElementById('tune-surface-hex');
  if (surfaceHex) surfaceHex.textContent = surface;

  const headFontSelect = document.getElementById('tune-heading-font');
  if (headFontSelect) headFontSelect.value = headingFont;

  const bodyFontSelect = document.getElementById('tune-body-font');
  if (bodyFontSelect) bodyFontSelect.value = bodyFont;
}

// ── Live Token Tuner Listeners ──────────────────────────────────────────────
function initTokenTuner() {
  const primaryInput = document.getElementById('tune-primary-color');
  const secondaryInput = document.getElementById('tune-secondary-color');
  const surfaceInput = document.getElementById('tune-surface-color');
  const headingFontSelect = document.getElementById('tune-heading-font');
  const bodyFontSelect = document.getElementById('tune-body-font');

  primaryInput.addEventListener('input', (e) => {
    const val = e.target.value;
    document.getElementById('tune-primary-hex').textContent = val;
    if (state.selectedTemplate) {
      state.selectedTemplate.theme.primary = val;
      renderLivePreview(state.selectedTemplate);
    }
  });

  secondaryInput.addEventListener('input', (e) => {
    const val = e.target.value;
    document.getElementById('tune-secondary-hex').textContent = val;
    if (state.selectedTemplate) {
      state.selectedTemplate.theme.secondary = val;
      renderLivePreview(state.selectedTemplate);
    }
  });

  surfaceInput.addEventListener('input', (e) => {
    const val = e.target.value;
    document.getElementById('tune-surface-hex').textContent = val;
    if (state.selectedTemplate) {
      state.selectedTemplate.theme.surface = val;
      renderLivePreview(state.selectedTemplate);
    }
  });

  headingFontSelect.addEventListener('change', (e) => {
    const val = e.target.value;
    if (state.selectedTemplate) {
      state.selectedTemplate.theme.headingFont = val;
      renderLivePreview(state.selectedTemplate);
    }
  });

  bodyFontSelect.addEventListener('change', (e) => {
    const val = e.target.value;
    if (state.selectedTemplate) {
      state.selectedTemplate.theme.font = val;
      renderLivePreview(state.selectedTemplate);
    }
  });
}

// ── Generator API Call ──────────────────────────────────────────────────────
async function generateTemplates() {
  const statusEl = document.getElementById('templates-status');
  const listEl = document.getElementById('archetypes-list');
  const btn = document.getElementById('btn-generate-templates');

  const brandName = document.getElementById('input-brand-name').value.trim() || 'Nova AI';
  const industry = document.getElementById('input-industry').value;
  const tagline = document.getElementById('input-tagline').value.trim();
  const offer = document.getElementById('input-offer').value.trim();
  const tone = document.getElementById('input-tone').value.trim();
  const useAi = document.getElementById('input-use-ai').checked;
  const lang = document.getElementById('studio-lang').value;

  btn.disabled = true;
  statusEl.innerHTML = '<span class="studio-badge" style="background:#0f6cbd;">⏳ 10 Awwwards Şablonu Oluşturuluyor...</span>';

  try {
    const res = await fetch('/api/generate-templates', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        brandName,
        industry,
        tagline,
        offer,
        tone,
        useAi,
        lang,
      }),
    });

    if (!res.ok) {
      if (res.status === 401) {
        showStudioAuth('Oturum süresi doldu. Lütfen tekrar giriş yapın.');
        return;
      }
      const err = await res.json().catch(() => ({ error: 'Üretim hatası' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }

    const data = await res.json();
    state.templates = data.templates || [];

    if (!state.templates.length) {
      throw new Error('Şablon listesi boş döndü.');
    }

    statusEl.innerHTML = '';
    const countBadge = document.getElementById('archetypes-count-badge');
    if (countBadge) countBadge.textContent = `${state.templates.length} Hazır`;

    // Map style hint tags
    const styleTags = {
      'dark-minimal-saas': 'Emporta Stili',
      'clean-soft-tech': 'Easeout Stili',
      'kinetic-editorial': 'Magazine Stili',
      'neo-brutalist': 'High-Energy',
      'glassmorphic-glow': 'Spatial Glow',
      'bento-grid': 'Apple Grid',
      'minimalist-swiss': 'Swiss Grid',
      'vibrant-aurora': 'Mesh Tech',
      'developer-monospace': 'Terminal Carbon',
      'warm-editorial': 'Editorial Serif',
    };

    // Render Cards
    listEl.innerHTML = state.templates
      .map(
        (tpl, idx) => `
      <div class="archetype-card ${idx === 0 ? 'selected' : ''}" data-tpl-index="${idx}">
        <div class="archetype-card-header">
          <div class="archetype-card-title-group">
            <span class="archetype-card-title">${escapeHtml(tpl.name)}</span>
            <span class="archetype-style-tag">${styleTags[tpl.id] || 'Tasarım'}</span>
          </div>
          <div class="archetype-swatches">
            <span class="color-dot" style="background: ${tpl.theme.primary};" title="Primary: ${tpl.theme.primary}"></span>
            <span class="color-dot" style="background: ${tpl.theme.secondary};" title="Secondary: ${tpl.theme.secondary}"></span>
            <span class="color-dot" style="background: ${tpl.theme.surface};" title="Surface: ${tpl.theme.surface}"></span>
          </div>
        </div>
        <p>${escapeHtml(tpl.description || tpl.hero?.headline || tpl.tagline)}</p>
        <div class="archetype-meta-tags">
          <span class="meta-chip">🔤 ${tpl.theme.headingFont} / ${tpl.theme.font}</span>
          <span class="meta-chip">📐 ${tpl.header.style}</span>
          <span class="meta-chip accent">🎬 ${tpl.motion?.scrollReveal || 'words'}</span>
        </div>
      </div>
    `,
      )
      .join('');

    // Wire Card Click
    listEl.querySelectorAll('.archetype-card').forEach((card) => {
      card.addEventListener('click', () => {
        listEl.querySelectorAll('.archetype-card').forEach((c) => c.classList.remove('selected'));
        card.classList.add('selected');
        const idx = Number(card.dataset.tplIndex);
        state.selectedTemplate = state.templates[idx];
        renderLivePreview(state.selectedTemplate);
      });
    });

    // Select first template immediately
    state.selectedTemplate = state.templates[0];
    renderLivePreview(state.selectedTemplate);
  } catch (err) {
    statusEl.innerHTML = `❌ <span style="color: #ef4444;">Hata: ${escapeHtml(err.message)}</span>`;
  } finally {
    btn.disabled = false;
  }
}

// ── Site Cloner API Call ────────────────────────────────────────────────────
async function extractSite() {
  const statusEl = document.getElementById('cloner-status');
  const summaryEl = document.getElementById('cloned-summary');
  const btn = document.getElementById('btn-extract-site');
  const url = document.getElementById('input-site-url').value.trim();

  if (!url) {
    statusEl.innerHTML = '<span style="color: #ef4444;">Lütfen geçerli bir URL girin.</span>';
    return;
  }

  btn.disabled = true;
  statusEl.innerHTML = '<span class="studio-badge" style="background:#0f6cbd;">🔍 Site Taranıyor & Stiller Kazınıyor...</span>';
  summaryEl.style.display = 'none';

  try {
    const res = await fetch('/api/extract-site', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    });

    if (!res.ok) {
      if (res.status === 401) {
        showStudioAuth('Oturum süresi doldu. Lütfen tekrar giriş yapın.');
        return;
      }
      const err = await res.json().catch(() => ({ error: 'Kazıma hatası' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }

    const data = await res.json();
    const profile = data?.profile || data || {};
    if (!profile || !profile.theme) {
      throw new Error('Web sitesi analizinden geçerli stil profili alınamadı.');
    }
    state.extractedProfile = profile;

    const brandLabel = profile.brandName || profile.title || profile.domain || 'Klonlanan Site';
    const primaryColor = profile.theme?.primary || '#0f6cbd';
    const secondaryColor = profile.theme?.secondary || '#111318';
    const surfaceColor = profile.theme?.surface || '#0b0d11';
    const headingFont = profile.theme?.headingFont || profile.theme?.font || 'Inter';
    const bodyFont = profile.theme?.font || 'Inter';
    const headerStyle = profile.header?.style || 'solid-full';
    const navCount = profile.header?.navLinks?.length || 0;
    const footerStyle = profile.footer?.style || 'minimal-stacked';
    const colCount = profile.footer?.columns?.length || 0;

    statusEl.innerHTML = `✅ <strong>"${escapeHtml(url)}" başarıyla analiz edildi!</strong>`;
    summaryEl.style.display = 'flex';

    summaryEl.innerHTML = `
      <div class="archetype-card selected" style="cursor: default;">
        <div class="archetype-card-header">
          <strong style="font-size: 0.95rem;">${escapeHtml(brandLabel)}</strong>
          <div class="archetype-swatches">
            <span class="color-dot" style="background: ${primaryColor};" title="Primary: ${primaryColor}"></span>
            <span class="color-dot" style="background: ${secondaryColor};" title="Secondary: ${secondaryColor}"></span>
            <span class="color-dot" style="background: ${surfaceColor};" title="Surface: ${surfaceColor}"></span>
          </div>
        </div>
        <div class="archetype-meta-tags">
          <span class="meta-chip">🔤 ${headingFont} / ${bodyFont}</span>
          <span class="meta-chip">📐 Header: ${headerStyle} (${navCount} link)</span>
          <span class="meta-chip">📑 Footer: ${footerStyle} (${colCount} kolon)</span>
        </div>
        <button type="button" class="studio-btn primary" id="btn-apply-cloned-preview" style="margin-top: 0.5rem; justify-content: center;">
          👀 Bu Tasarımı Canlı Önizle
        </button>
      </div>
    `;

    document.getElementById('btn-apply-cloned-preview')?.addEventListener('click', () => {
      state.selectedTemplate = profile;
      renderLivePreview(profile);
    });

    // Automatically preview cloned site
    state.selectedTemplate = profile;
    renderLivePreview(profile);
  } catch (err) {
    statusEl.innerHTML = `❌ <span style="color: #ef4444;">Hata: ${escapeHtml(err.message)}</span>`;
  } finally {
    btn.disabled = false;
  }
}

// ── JSON Modal & Brand Creation ─────────────────────────────────────────────
function initJsonModal() {
  const modal = document.getElementById('json-modal');
  const viewBtn = document.getElementById('btn-view-json');
  const closeBtn = document.getElementById('btn-close-json');
  const copyBtn = document.getElementById('btn-copy-json');
  const codeEl = document.getElementById('json-modal-code');

  viewBtn.addEventListener('click', () => {
    if (!state.selectedTemplate) {
      alert('Lütfen önce bir şablon üretin veya web sitesi klonlayın.');
      return;
    }
    codeEl.textContent = JSON.stringify(state.selectedTemplate, null, 2);
    modal.classList.add('active');
  });

  closeBtn.addEventListener('click', () => {
    modal.classList.remove('active');
  });

  modal.addEventListener('click', (e) => {
    if (e.target === modal) modal.classList.remove('active');
  });

  copyBtn.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(codeEl.textContent);
      copyBtn.textContent = '✓ Kopyalandı!';
      setTimeout(() => {
        copyBtn.textContent = '📋 Panoya Kopyala';
      }, 1800);
    } catch {
      alert('Panoya kopyalanamadı.');
    }
  });

  // Save as Brand Button
  document.getElementById('btn-create-brand').addEventListener('click', async () => {
    if (!state.selectedTemplate) {
      alert('Önce bir şablon üretin veya seçin.');
      return;
    }
    const tpl = state.selectedTemplate;
    const brandId = (tpl.id || tpl.brandName || 'nova-brand')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

    const brandPayload = {
      id: brandId,
      domain: `${brandId}.example.com`,
      languages: [state.lang || 'en'],
      primaryCta: tpl.primaryCta || tpl.header?.cta?.label || 'Get Started',
      tone: tpl.tone || 'Clear and credible',
      theme: tpl.theme,
      header: tpl.header,
      footer: tpl.footer,
      generationBrief: {
        industry: document.getElementById('input-industry')?.value || 'software',
        audience: 'Modern teams and decision makers',
        offer: tpl.offer || 'AI Landing Page Platform',
        objective: 'Drive qualified demo requests',
        differentiators: ['GSAP animations', 'Awwwards quality design', 'Lightning speed'],
        designReferences: [],
      },
      activeTiers: ['T1'],
    };

    try {
      const res = await fetch('/api/brands', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(brandPayload),
      });

      if (!res.ok) {
        if (res.status === 401) {
          showStudioAuth('Oturum süresi doldu. Lütfen tekrar giriş yapın.');
          return;
        }
        const err = await res.json().catch(() => ({ error: 'Kayıt hatası' }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      alert(`🎉 "${brandId}" markası başarıyla oluşturuldu! Şimdi admin paneline yönlendiriliyorsunuz.`);
      window.location.href = '/#brands';
    } catch (err) {
      alert(`Marka kaydedilemedi: ${err.message}`);
    }
  });
}

function applyStudioTranslations() {
  const isEn = state.lang === 'en';

  const tabTpl = document.getElementById('tab-btn-templates');
  if (tabTpl) tabTpl.textContent = isEn ? '✨ 10 Awwwards Templates' : '✨ 10 Awwwards Şablonu';

  const tabCloner = document.getElementById('tab-btn-cloner');
  if (tabCloner) tabCloner.textContent = isEn ? '🌐 Clone Website' : '🌐 Web Sitesi Klonla';

  const viewJsonBtn = document.getElementById('btn-view-json');
  if (viewJsonBtn) viewJsonBtn.textContent = isEn ? '📋 Inspect JSON' : '📋 JSON İncele';

  const createBrandBtn = document.getElementById('btn-create-brand');
  if (createBrandBtn) createBrandBtn.textContent = isEn ? '🚀 Save as Brand' : '🚀 Marka Olarak Kaydet';

  const briefTitle = document.querySelector('.brief-config-title');
  if (briefTitle) briefTitle.textContent = isEn ? '⚙️ Brand & Brief Settings' : '⚙️ Marka & Brief Ayarları';

  const labelBrandName = document.querySelector('label[for="input-brand-name"]');
  if (labelBrandName) labelBrandName.textContent = isEn ? 'Brand Name' : 'Marka Adı';

  const labelIndustry = document.querySelector('label[for="input-industry"]');
  if (labelIndustry) labelIndustry.textContent = isEn ? 'Industry' : 'Sektör';

  const labelTagline = document.querySelector('label[for="input-tagline"]');
  if (labelTagline) labelTagline.textContent = isEn ? 'Tagline / Slogan' : 'Slogan';

  const labelOffer = document.querySelector('label[for="input-offer"]');
  if (labelOffer) labelOffer.textContent = isEn ? 'Target Audience & Offer' : 'Hedef Kitle / Teklif';

  const labelTone = document.querySelector('label[for="input-tone"]');
  if (labelTone) labelTone.textContent = isEn ? 'Brand Tone' : 'Marka Tonu';

  const aiCheckLabel = document.querySelector('.checkbox-label span');
  if (aiCheckLabel) aiCheckLabel.textContent = isEn ? 'Enrich copy with AI (OpenRouter)' : 'Yapay zeka ile metinleri zenginleştir (OpenRouter)';

  const genBtn = document.getElementById('btn-generate-templates');
  if (genBtn) genBtn.textContent = isEn ? '🚀 Generate 10 Awwwards Templates' : '🚀 10 Awwwards Şablonunu Oluştur';

  const labelUrl = document.querySelector('label[for="input-site-url"]');
  if (labelUrl) labelUrl.textContent = isEn ? 'Current Website Address (URL)' : 'Mevcut Web Sitesi Adresi (URL)';

  const presetLabel = document.querySelector('#pane-cloner .form-group span');
  if (presetLabel) presetLabel.textContent = isEn ? 'Quick Demo Presets:' : 'Hızlı Demo Örnekleri:';

  const extractBtn = document.getElementById('btn-extract-site');
  if (extractBtn) extractBtn.textContent = isEn ? '🔍 Analyze Website & Clone Style' : '🔍 Web Sitesini Analiz Et ve Stili Klonla';

  const btnDesktop = document.querySelector('.device-btn[data-device="desktop"]');
  if (btnDesktop) btnDesktop.textContent = isEn ? 'Desktop (1280px)' : 'Masaüstü (1280px)';

  const btnTablet = document.querySelector('.device-btn[data-device="tablet"]');
  if (btnTablet) btnTablet.textContent = isEn ? 'Tablet (768px)' : 'Tablet (768px)';

  const btnMobile = document.querySelector('.device-btn[data-device="mobile"]');
  if (btnMobile) btnMobile.textContent = isEn ? 'iPhone 16 Pro (390px)' : 'iPhone 16 Pro (390px)';

  const tunerLabels = document.querySelectorAll('.tune-chip-label');
  if (tunerLabels.length >= 5) {
    tunerLabels[0].textContent = isEn ? 'Primary' : 'Birincil';
    tunerLabels[1].textContent = isEn ? 'Secondary' : 'İkincil';
    tunerLabels[2].textContent = isEn ? 'Surface' : 'Yüzey';
    tunerLabels[3].textContent = isEn ? 'Heading Font' : 'Başlık Fontu';
    tunerLabels[4].textContent = isEn ? 'Body Font' : 'Gövde Fontu';
  }

  const modalHeader = document.querySelector('.json-modal-header strong');
  if (modalHeader) modalHeader.textContent = isEn ? '📋 Selected Design Template JSON Contract' : '📋 Seçili Tasarım Şablonu JSON Sözleşmesi';

  const closeJsonBtn = document.getElementById('btn-close-json');
  if (closeJsonBtn) closeJsonBtn.textContent = isEn ? '✕ Close' : '✕ Kapat';

  const copyJsonBtn = document.getElementById('btn-copy-json');
  if (copyJsonBtn) copyJsonBtn.textContent = isEn ? '📋 Copy to Clipboard' : '📋 Panoya Kopyala';
}

// ── Initialize Studio ───────────────────────────────────────────────────────
function initStudioApp() {
  state.lang = localStorage.getItem('pagix-admin-lang') || localStorage.getItem('landcraft-admin-lang') || 'en';
  const langSelect = document.getElementById('studio-lang');
  if (langSelect) langSelect.value = state.lang;
  applyStudioTranslations();

  initTabs();
  initBriefToggle();
  initDeviceSwitcher();
  initTokenTuner();
  initJsonModal();

  document.getElementById('btn-generate-templates').addEventListener('click', generateTemplates);
  document.getElementById('btn-extract-site').addEventListener('click', extractSite);

  // Theme Toggle
  const themeBtn = document.getElementById('studio-theme-toggle');
  themeBtn.addEventListener('click', () => {
    const isDark = document.documentElement.dataset.theme === 'dark';
    document.documentElement.dataset.theme = isDark ? 'light' : 'dark';
  });

  // Language Change
  document.getElementById('studio-lang').addEventListener('change', (e) => {
    state.lang = e.target.value;
    localStorage.setItem('pagix-admin-lang', state.lang);
    applyStudioTranslations();
    if (state.selectedTemplate) {
      renderLivePreview(state.selectedTemplate);
    }
  });

  // Automatically trigger first generation on load for instant preview
  generateTemplates();
}

// ── Auth Gate ────────────────────────────────────────────────────────────
// Bu sayfa (Şablon Stüdyosu) admin panelin oturum sistemini kullanır. Sayfa
// önce /api/auth/session ile oturum kontrolü yapar; oturum yoksa admin
// panelindekiyle tutarlı bir uygulama-içi giriş formu gösterir — böylece
// /api/generate-templates gibi çağrılar sessizce 401 ile başarısız olup
// tarayıcının native Basic Auth penceresini tetiklemek yerine, kullanıcı
// net bir giriş ekranı görür.
function showStudioAuth(message = '') {
  const authShell = document.getElementById('studio-auth-shell');
  const shell = document.getElementById('studio-shell');
  const errorEl = document.getElementById('studio-auth-error');
  if (authShell) authShell.hidden = false;
  if (shell) shell.hidden = true;
  if (errorEl) {
    if (message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    } else {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }
}

function showStudioApp() {
  const authShell = document.getElementById('studio-auth-shell');
  const shell = document.getElementById('studio-shell');
  if (authShell) authShell.hidden = true;
  if (shell) shell.hidden = false;
}

async function checkStudioAuth() {
  try {
    const res = await fetch('/api/auth/session');
    const data = await res.json().catch(() => ({}));
    return Boolean(data.authenticated);
  } catch {
    return false;
  }
}

function initStudioAuthForm() {
  const form = document.getElementById('studio-auth-form');
  if (!form) return;
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submitBtn = document.getElementById('studio-auth-submit');
    const username = document.getElementById('studio-auth-username').value.trim();
    const password = document.getElementById('studio-auth-password').value;
    submitBtn.disabled = true;
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      showStudioApp();
      if (!document.body.dataset.studioAppInitialized) {
        document.body.dataset.studioAppInitialized = 'true';
        initStudioApp();
      }
    } catch (err) {
      showStudioAuth(err.message || 'Geçersiz kullanıcı adı veya şifre.');
    } finally {
      submitBtn.disabled = false;
    }
  });
}

document.addEventListener('DOMContentLoaded', async () => {
  initStudioAuthForm();
  const authenticated = await checkStudioAuth();
  if (authenticated) {
    document.body.dataset.studioAppInitialized = 'true';
    showStudioApp();
    initStudioApp();
  } else {
    showStudioAuth();
  }
});
